import { useState } from "react";
import {
  CheckCircle,
  Download,
  ArrowLeft,
  Video,
  Film,
  Crown,
  Sparkles,
  Flag,
  Wand2,
  Trash2,
  Loader2,
} from "lucide-react";
import { getDisplayUrl, type PlanType } from "@/stores/user-store";
import { ReportContentModal } from "@/components/features/report/report-content-modal";
import { NameCharacterModal } from "@/components/features/characters";
import { useDeleteAsset } from "@/hooks";
import type { Generation, ModelId } from "@/types";

interface GenerationSuccessStateProps {
  generation: Generation;
  generationId: string;
  plan: PlanType;
  modelId?: ModelId;
  hasCreatorAccess: boolean;
  onDownload: () => void;
  onViewAssets: () => void;
  onEditImage: () => void;
  onAnimateImage: () => void;
  onGenerateAnother: () => void;
  onExtendVideo: () => void;
  /** Navigate back to a character detail page (only used in character context). */
  onViewCharacter?: (folder: string) => void;
  /** Tweak the character again — opens edit form with this image as source. */
  onTweakAgain?: (folder: string) => void;
  /** Animate the character with this image. */
  onAnimateCharacter?: (folder: string) => void;
  onUpgradeClick?: () => void;
  /**
   * The image was produced via the character creation flow but hasn't
   * been Saved yet — strips the page down to the two decisions that
   * matter (Save / Edit) and reveals the edit prompt inline rather
   * than navigating to a separate page.
   */
  isCharacterDraft?: boolean;
  /**
   * Promote this image into a saved character. Receives the
   * user-chosen name from the Save modal; the parent forwards it to
   * the save endpoint (server falls back to "Character N" if blank).
   */
  onSaveCharacter?: (name: string) => void;
  /** Spinner state while the Save call is in flight. */
  isSaving?: boolean;
  /** Inline error from the save call (e.g., duplicate-name 409). */
  saveError?: string | null;
  /**
   * Reset any persisted save-error so a previous failure doesn't show
   * up briefly when the modal reopens. Called by the success state on
   * modal open.
   */
  onClearSaveError?: () => void;
  /** Pre-filled suggestion in the Save modal (e.g., "Character 3"). */
  suggestedCharacterName?: string;
  /**
   * Apply an inline character-draft edit. Receives the user's edit
   * instruction (e.g. "make her hair red"); the parent navigates to
   * `/generation/new` with `qwen_image_edit` + the current generation
   * as the source, keeping `is_character_draft` set so the result also
   * lands in this same minimal Save / Edit UI.
   */
  onEditWithPrompt?: (prompt: string) => void;
}

export function GenerationSuccessState({
  generation,
  plan,
  modelId,
  hasCreatorAccess,
  onDownload,
  onViewAssets,
  onEditImage,
  onAnimateImage,
  onGenerateAnother,
  onExtendVideo,
  onViewCharacter,
  onTweakAgain,
  onAnimateCharacter,
  onUpgradeClick,
  isCharacterDraft,
  onSaveCharacter,
  isSaving,
  saveError,
  onClearSaveError,
  suggestedCharacterName,
  onEditWithPrompt,
}: GenerationSuccessStateProps) {
  const isFreePlan = plan === "free";
  const [showReport, setShowReport] = useState(false);
  const deleteAsset = useDeleteAsset();
  const isImage = generation.media_type === "IMAGE";
  const isVideo = generation.media_type === "VIDEO";
  const inCharacterContext = !!generation.folder;
  // Inline edit panel for the character-draft branch.
  const [isEditing, setIsEditing] = useState(false);
  const [editPrompt, setEditPrompt] = useState("");
  // Save-as-Character name modal.
  const [showSaveModal, setShowSaveModal] = useState(false);
  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 md:p-6">
      <div className="w-full max-w-2xl">
        {/* Success Header */}
        <div className="text-center mb-4 md:mb-6">
          <div className="inline-flex items-center justify-center p-3 rounded-full bg-green-500/10 mb-2">
            <CheckCircle className="size-8 md:size-10 text-green-400" />
          </div>
          <h1 className="text-xl md:text-2xl font-bold text-white mb-1">
            Generation Complete!
          </h1>
          <p className="text-sm text-[#888]">
            Your {generation.media_type.toLowerCase()} is ready
          </p>
        </div>

        {/* Preview */}
        <div className="rounded-2xl overflow-hidden bg-black border border-white/[0.1] mb-4 md:mb-5">
          {generation.media_type === "VIDEO" ? (
            <video
              src={getDisplayUrl(
                {
                  url: generation.url!,
                  url_watermarked: generation.url_watermarked,
                },
                plan,
              )}
              controls
              autoPlay
              loop
              className="w-full aspect-video object-contain"
            />
          ) : (
            <img
              src={getDisplayUrl(
                {
                  url: generation.url!,
                  url_watermarked: generation.url_watermarked,
                },
                plan,
              )}
              alt="Generated image"
              className="w-full object-contain max-h-[50vh] md:max-h-[45vh]"
            />
          )}
        </div>

        {generation.prompt && modelId !== "qwen_image_edit" && (
          <div className="mb-4 p-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
            <p className="text-xs text-[#666] mb-1">Prompt</p>
            <p className="text-sm text-[#ccc] line-clamp-2">
              {generation.prompt}
            </p>
          </div>
        )}

        {/* Primary Actions — hidden for character drafts: Download/View
            don't apply to an unsaved draft and they crowd out the only
            two decisions that matter (Save / Edit). */}
        {!isCharacterDraft && (
          <div className="flex gap-3 mb-3">
            <button
              onClick={onDownload}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity text-sm md:text-base"
            >
              <Download className="size-4 md:size-5" />
              Download
            </button>
            <button
              onClick={onViewAssets}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base"
            >
              <ArrowLeft className="size-4 md:size-5" />
              View All Assets
            </button>
          </div>
        )}

        {/* Secondary actions branch into five states:
            (1a) IMAGE no folder, character draft → Save Character | Edit | Try Again | Discard
                                     Came from /characters; user explicitly
                                     chooses whether to keep this draft.
            (1b) IMAGE no folder, plain T2I       → Edit Image | Animate Image
                                     Generic image flow stays unchanged.
            (2)  IMAGE with folder                → Keep & View | Edit Character | Animate Character | Discard
                                     The tight tweak loop on a character.
                                     Discard soft-deletes this generation so
                                     the canonical falls back to the previous
                                     version (or the character disappears
                                     entirely if this was the first image).
            (3)  VIDEO no folder                  → Generate Another | Extend Video
            (4)  VIDEO with folder                → View Character | Animate Again | Extend Video */}
        {isImage && !inCharacterContext && isCharacterDraft && (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                // Clear any leftover error from a previous attempt
                // before reopening, so the modal never flashes a stale
                // 409 message when the user comes back to retry.
                onClearSaveError?.();
                setShowSaveModal(true);
              }}
              disabled={isSaving}
              className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 disabled:opacity-60 transition-opacity text-sm md:text-base"
            >
              {isSaving ? (
                <Loader2 className="size-4 md:size-5 animate-spin" />
              ) : (
                <CheckCircle className="size-4 md:size-5" />
              )}
              {isSaving ? "Saving..." : "Save Character"}
            </button>

            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
              >
                <Wand2 className="size-4 md:size-5" />
                Edit
              </button>
            ) : (
              <div className="flex flex-col gap-2 p-3 rounded-xl bg-white/[0.03] border border-white/[0.08]">
                <textarea
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  placeholder="Describe the change. e.g., make her hair red, change to red lingerie, swap the background to a beach..."
                  rows={2}
                  autoFocus
                  className="w-full resize-none bg-transparent outline-none text-sm text-white placeholder:text-[#555]"
                />
                <div className="flex justify-end gap-2">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setEditPrompt("");
                    }}
                    className="px-4 py-2 rounded-lg text-xs md:text-sm text-[#888] hover:text-white hover:bg-white/[0.05] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      const trimmed = editPrompt.trim();
                      if (!trimmed) return;
                      onEditWithPrompt?.(trimmed);
                    }}
                    disabled={!editPrompt.trim()}
                    className="px-4 py-2 rounded-lg text-xs md:text-sm bg-white/[0.08] text-white hover:bg-white/[0.12] disabled:opacity-40 disabled:cursor-not-allowed transition-colors border border-white/[0.08]"
                  >
                    Apply edit
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {isImage && !inCharacterContext && !isCharacterDraft && (
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onEditImage}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
            >
              <Wand2 className="size-4 md:size-5" />
              Edit Image
            </button>
            <button
              onClick={onAnimateImage}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
            >
              <Video className="size-4 md:size-5" />
              Animate Image
            </button>
          </div>
        )}

        {isImage && inCharacterContext && (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => onViewCharacter?.(generation.folder!)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity text-sm md:text-base"
            >
              <CheckCircle className="size-4 md:size-5" />
              Keep & View Character
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => onTweakAgain?.(generation.folder!)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
              >
                <Wand2 className="size-4 md:size-5" />
                Edit Character
              </button>
              <button
                onClick={() => onAnimateCharacter?.(generation.folder!)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
              >
                <Video className="size-4 md:size-5" />
                Animate Character
              </button>
            </div>
            <button
              onClick={async () => {
                if (deleteAsset.isPending) return;
                try {
                  await deleteAsset.mutateAsync(generation.id);
                  onViewCharacter?.(generation.folder!);
                } catch {
                  // Silent — generation still exists, user can click again.
                }
              }}
              disabled={deleteAsset.isPending}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs md:text-sm text-red-300/80 hover:text-red-200 hover:bg-red-500/5 disabled:opacity-50 transition-colors"
            >
              {deleteAsset.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Trash2 className="size-4" />
              )}
              Discard
            </button>
          </div>
        )}

        {isVideo && !inCharacterContext && (
          <div className="flex gap-3">
            <button
              onClick={onGenerateAnother}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
            >
              <Video className="size-4 md:size-5" />
              Generate Another
            </button>
            <button
              onClick={onExtendVideo}
              className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl text-sm md:text-base border transition-colors ${
                hasCreatorAccess
                  ? "bg-white/[0.05] text-white hover:bg-white/[0.1] border-white/[0.08]"
                  : "bg-violet-500/10 text-violet-300 border-violet-500/30 hover:bg-violet-500/20"
              }`}
            >
              <Film className="size-4 md:size-5" />
              Extend Video
              {!hasCreatorAccess && (
                <Crown className="size-4 text-violet-400 drop-shadow-[0_0_4px_rgba(139,92,246,0.6)]" />
              )}
            </button>
          </div>
        )}

        {isVideo && inCharacterContext && (
          <div className="flex flex-col gap-3">
            <button
              onClick={() => onViewCharacter?.(generation.folder!)}
              className="flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-gradient-to-r from-violet-500 to-purple-500 text-white hover:opacity-90 transition-opacity text-sm md:text-base"
            >
              <CheckCircle className="size-4 md:size-5" />
              View Character
            </button>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => onAnimateCharacter?.(generation.folder!)}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl bg-white/[0.05] text-white hover:bg-white/[0.1] transition-colors text-sm md:text-base border border-white/[0.08]"
              >
                <Video className="size-4 md:size-5" />
                Animate Again
              </button>
              <button
                onClick={onExtendVideo}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 md:px-6 md:py-3 rounded-xl text-sm md:text-base border transition-colors ${
                  hasCreatorAccess
                    ? "bg-white/[0.05] text-white hover:bg-white/[0.1] border-white/[0.08]"
                    : "bg-violet-500/10 text-violet-300 border-violet-500/30 hover:bg-violet-500/20"
                }`}
              >
                <Film className="size-4 md:size-5" />
                Extend Video
                {!hasCreatorAccess && (
                  <Crown className="size-4 text-violet-400 drop-shadow-[0_0_4px_rgba(139,92,246,0.6)]" />
                )}
              </button>
            </div>
          </div>
        )}

        {/* Upgrade CTA for free plan users */}
        {isFreePlan && onUpgradeClick && (
          <button
            onClick={onUpgradeClick}
            className="w-full mt-4 flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border border-amber-500/20 hover:border-amber-500/40 hover:from-amber-500/15 hover:via-orange-500/15 hover:to-amber-500/15 transition-all group"
          >
            <Sparkles className="size-4 text-amber-400 group-hover:scale-110 transition-transform" />
            <span className="text-sm font-medium text-amber-200/90">
              Upgrade to remove watermarks on generations
            </span>
          </button>
        )}

        <button
          onClick={() => setShowReport(true)}
          className="w-full mt-3 flex items-center justify-center gap-1.5 py-2 text-xs text-[#555] hover:text-[#888] transition-colors"
        >
          <Flag className="size-3" />
          Report this content
        </button>

        <ReportContentModal
          isOpen={showReport}
          onClose={() => setShowReport(false)}
          generationId={generation.id}
        />

        <NameCharacterModal
          isOpen={showSaveModal}
          onClose={() => {
            // Block close while a save is in flight so the user can't
            // double-fire by closing-and-reopening between request and
            // navigation.
            if (isSaving) return;
            setShowSaveModal(false);
          }}
          onSubmit={(name) => onSaveCharacter?.(name)}
          title="Save Character"
          submitLabel="Save"
          defaultName={suggestedCharacterName}
          isSubmitting={isSaving}
          error={saveError}
        />
      </div>
    </div>
  );
}
